import { useEffect, useState, type RefObject } from "react";
import { ChevronLeft, ChevronRight, FileDown, Minus, Plus } from "lucide-react";

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5] as const;
const DEFAULT_ZOOM_INDEX = 2;

interface ReportPreviewToolbarProps {
  /** The scroll container holding the rendered report pages. */
  previewRef: RefObject<HTMLDivElement | null>;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  /** Same server-generated PDF as the header button — deliberately not
   * window.print(), so there is only ever one definition of "the export". */
  onDownload: () => void;
  downloadDisabled: boolean;
}

export function ReportPreviewToolbar({
  previewRef,
  zoom,
  onZoomChange,
  onDownload,
  downloadDisabled,
}: ReportPreviewToolbarProps) {
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState(1);

  // Page count comes from the rendered tree rather than a constant so it
  // can't drift from ClimateRiskReport's actual section list.
  useEffect(() => {
    const pages = previewRef.current?.querySelectorAll("[data-report-page]");
    setPageCount(pages?.length ?? 0);
  }, [previewRef, zoom]);

  function goTo(next: number) {
    const clamped = Math.min(Math.max(next, 1), pageCount || 1);
    setPage(clamped);
    const pages = previewRef.current?.querySelectorAll("[data-report-page]");
    pages?.[clamped - 1]?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const zoomIndex = ZOOM_STEPS.indexOf(zoom as (typeof ZOOM_STEPS)[number]);
  const safeZoomIndex = zoomIndex === -1 ? DEFAULT_ZOOM_INDEX : zoomIndex;

  return (
    <div className="mx-auto flex w-full max-w-[210mm] flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card/70 px-4 py-2">
      <span className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-primary" />
        <span className="text-xs uppercase leading-4 tracking-[0.08em] text-muted-foreground">
          PDF export preview — A4 portrait
        </span>
      </span>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => goTo(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
            className="rounded-sm p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="text-xs font-medium tabular-nums">
            Page {page} of {pageCount || "—"}
          </span>
          <button
            type="button"
            onClick={() => goTo(page + 1)}
            disabled={pageCount === 0 || page >= pageCount}
            aria-label="Next page"
            className="rounded-sm p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <span className="h-3.5 w-px bg-border" />

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onZoomChange(ZOOM_STEPS[Math.max(safeZoomIndex - 1, 0)])}
            disabled={safeZoomIndex === 0}
            aria-label="Zoom out"
            className="rounded-sm p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="w-10 text-center text-xs tabular-nums text-muted-foreground">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => onZoomChange(ZOOM_STEPS[Math.min(safeZoomIndex + 1, ZOOM_STEPS.length - 1)])}
            disabled={safeZoomIndex === ZOOM_STEPS.length - 1}
            aria-label="Zoom in"
            className="rounded-sm p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>

        <span className="h-3.5 w-px bg-border" />

        <button
          type="button"
          onClick={onDownload}
          disabled={downloadDisabled}
          aria-label="Download PDF"
          title="Download PDF"
          className="rounded-sm p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
        >
          <FileDown className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
